/**
 * Full-page workspace graph (and local neighbourhood when `centerWsPath` is set).
 */
import { useCoreServices } from '@bangle.io/context';
import {
  buildLocalGraph,
  toGraphResponse,
  type GraphResponse,
} from '@bangle.io/service-core';
import { useAtomValue } from 'jotai';
import {
  Maximize,
  Minus,
  Plus,
  SlidersHorizontal,
  Waypoints,
} from 'lucide-react';
import React, { lazy, Suspense, useMemo, useRef, useState } from 'react';
import {
  buildGraphView,
  GraphControls,
  isPageNode,
  useGraphSettings,
  type GraphCanvasHandle,
  type ViewNode,
} from '../graph';
import { AppHeader } from '../layout/app-header';
import { PageContentContainer } from '../layout/main-content-container';

const GraphCanvas = lazy(() =>
  import('../graph/GraphCanvas').then((m) => ({ default: m.GraphCanvas })),
);

const iconButton =
  'flex h-8 w-8 items-center justify-center rounded-md border border-border bg-popover/90 text-muted-foreground shadow-sm hover:bg-muted hover:text-foreground';

export function PageWsGraph() {
  const coreServices = useCoreServices();
  const routeInfo = useAtomValue(coreServices.navigation.$routeInfo);
  const noteLinkGraph = useAtomValue(
    coreServices.workspaceState.$noteLinkGraph,
  );

  const wsName =
    routeInfo.route === 'ws-graph' ? routeInfo.payload.wsName : undefined;
  const centerWsPath =
    routeInfo.route === 'ws-graph'
      ? routeInfo.payload.centerWsPath
      : undefined;

  const [settings, update] = useGraphSettings(wsName ?? '');
  const [controlsOpen, setControlsOpen] = useState(false);
  const [selected, setSelected] = useState<ViewNode | null>(null);
  const canvasRef = useRef<GraphCanvasHandle>(null);

  const data: GraphResponse | null = useMemo(() => {
    if (!wsName || noteLinkGraph.status !== 'ready') {
      return null;
    }
    if (centerWsPath) {
      return buildLocalGraph(noteLinkGraph.graph, centerWsPath, {
        depth: settings.depth,
        direction: settings.direction,
      });
    }
    return toGraphResponse(noteLinkGraph.graph);
  }, [
    centerWsPath,
    noteLinkGraph,
    settings.depth,
    settings.direction,
    wsName,
  ]);

  const groupsKey = JSON.stringify(settings.groups);
  const view = useMemo(
    () =>
      data
        ? buildGraphView(data, settings, data.center, {
            rootFolder: t.app.graph.groups.rootFolder,
          })
        : { nodes: [], links: [], legend: [] },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data,
      settings.search,
      settings.showOrphans,
      settings.colorBy,
      groupsKey,
      settings.sizeByDegree,
      settings.nodeSize,
    ],
  );

  const open = (node: ViewNode) => {
    if (isPageNode(node)) {
      coreServices.navigation.goWsFile(node.id);
    }
  };

  const pageCount = view.nodes.filter(isPageNode).length;
  const linkCount = view.links.length;
  const isLoading =
    noteLinkGraph.status === 'loading' || noteLinkGraph.status === 'idle';
  const hasError = noteLinkGraph.status === 'error';
  const centerTitle = centerWsPath
    ? data?.nodes.find((n) => n.id === centerWsPath)?.title
    : undefined;

  if (!wsName) {
    return null;
  }

  return (
    <>
      <AppHeader />
      <PageContentContainer
        testId="page-ws-graph"
        applyPadding={false}
        constrainContentWidth={false}
      >
        <div className="relative h-full min-h-[320px] w-full flex-1 overflow-hidden bg-muted/20">
          {data && (
            <Suspense fallback={null}>
              <GraphCanvas
                key={centerWsPath ?? 'space'}
                nodes={view.nodes}
                links={view.links}
                settings={settings}
                onOpen={open}
                onSelect={setSelected}
                selectedId={selected?.id ?? null}
                ariaLabel={t.app.graph.aria({
                  nodes: view.nodes.length,
                  links: view.links.length,
                })}
                handleRef={canvasRef}
              />
            </Suspense>
          )}

          <div className="pointer-events-none absolute top-3 left-3 flex max-w-[calc(100%-10.5rem)] flex-col gap-1 sm:max-w-md">
            <div className="pointer-events-auto flex flex-wrap items-center gap-2 rounded-lg border border-border bg-popover/90 px-2.5 py-1.5 text-muted-foreground text-xs shadow-sm backdrop-blur">
              <Waypoints
                size={14}
                className="shrink-0 text-primary"
                aria-hidden="true"
              />
              <span className="font-medium text-foreground">
                {centerWsPath
                  ? centerTitle
                    ? t.app.graph.local.of({ title: centerTitle })
                    : t.app.graph.local.title
                  : t.app.graph.title}
              </span>
              {data && (
                <span className="text-muted-foreground tabular-nums">
                  {t.app.graph.counts({
                    pages: pageCount,
                    links: linkCount,
                  })}
                </span>
              )}
              {centerWsPath ? (
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() =>
                    coreServices.navigation.goGraph({ wsName })
                  }
                >
                  {t.app.graph.local.wholeSpace}
                </button>
              ) : null}
            </div>
            {data?.truncated && (
              <div
                role="status"
                className="pointer-events-auto rounded-lg border border-amber-300 bg-amber-50/95 px-2.5 py-1.5 text-amber-900 text-xs dark:border-amber-800 dark:bg-amber-950/90 dark:text-amber-200"
              >
                {t.app.graph.truncated({
                  shown: data.limit,
                  total: data.totalPages,
                })}
              </div>
            )}
            {isLoading && (
              <div className="pointer-events-auto text-muted-foreground text-xs">
                {t.app.graph.loading}
              </div>
            )}
            {hasError && (
              <div
                role="alert"
                className="pointer-events-auto rounded-lg border border-destructive/40 bg-destructive/10 px-2.5 py-1.5 text-destructive text-xs"
              >
                {t.app.graph.error}
              </div>
            )}
            {data && view.nodes.length === 0 && (
              <div className="pointer-events-auto text-muted-foreground text-xs">
                {t.app.graph.empty}
              </div>
            )}
          </div>

          <div className="pointer-events-none absolute top-3 right-3 flex max-h-[calc(100%-1.5rem)] w-72 max-w-[calc(100%-1.5rem)] flex-col items-end gap-2">
            <div className="pointer-events-auto flex gap-1">
              <button
                type="button"
                className={iconButton}
                onClick={() => canvasRef.current?.zoomBy(1.3)}
                aria-label={t.app.graph.zoomIn}
                title={t.app.graph.zoomIn}
              >
                <Plus size={15} />
              </button>
              <button
                type="button"
                className={iconButton}
                onClick={() => canvasRef.current?.zoomBy(1 / 1.3)}
                aria-label={t.app.graph.zoomOut}
                title={t.app.graph.zoomOut}
              >
                <Minus size={15} />
              </button>
              <button
                type="button"
                className={iconButton}
                onClick={() => canvasRef.current?.fit()}
                aria-label={t.app.graph.fit}
                title={t.app.graph.fit}
              >
                <Maximize size={15} />
              </button>
              <button
                type="button"
                className={`${iconButton} ${controlsOpen ? 'ring-2 ring-ring/60' : ''}`}
                onClick={() => setControlsOpen((v) => !v)}
                aria-expanded={controlsOpen}
                aria-label={t.app.graph.settings}
                title={t.app.graph.settings}
              >
                <SlidersHorizontal size={15} />
              </button>
            </div>
            {controlsOpen && (
              <div className="pointer-events-auto min-h-0 w-full">
                <GraphControls
                  settings={settings}
                  onChange={update}
                  local={!!centerWsPath}
                  onClose={() => setControlsOpen(false)}
                />
              </div>
            )}
          </div>

          {view.legend.length > 0 && (
            <div className="absolute bottom-3 left-3 flex max-w-[calc(100%-1.5rem)] flex-wrap gap-1 sm:max-w-xl">
              {view.legend.map((entry) => {
                const active =
                  !!entry.query && settings.search.trim() === entry.query;
                return (
                  <button
                    key={`${entry.label}-${entry.color}`}
                    type="button"
                    disabled={!entry.query}
                    onClick={() =>
                      entry.query &&
                      update({ search: active ? '' : entry.query })
                    }
                    title={
                      entry.query
                        ? t.app.graph.legendFilter({ query: entry.query })
                        : undefined
                    }
                    className={`flex items-center gap-1.5 rounded-full border bg-popover/90 px-2 py-0.5 text-[11px] text-foreground shadow-sm backdrop-blur enabled:hover:bg-muted ${
                      active
                        ? 'border-primary'
                        : 'border-border'
                    }`}
                  >
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ background: entry.color }}
                      aria-hidden="true"
                    />
                    <span className="max-w-40 truncate">{entry.label}</span>
                    <span className="text-muted-foreground tabular-nums">
                      {entry.count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {selected && (
            <div className="absolute inset-x-3 bottom-12 mx-auto flex max-w-sm items-center gap-2 rounded-lg border border-border bg-popover px-3 py-2 text-sm shadow-lg">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: selected.color }}
                aria-hidden="true"
              />
              <span
                className="min-w-0 flex-1 truncate text-foreground"
                title={selected.path}
              >
                {selected.title}
              </span>
              {isPageNode(selected) && (
                <>
                  <button
                    type="button"
                    onClick={() => open(selected)}
                    className="shrink-0 rounded bg-primary px-2.5 py-1 font-medium text-primary-foreground text-xs hover:opacity-90"
                  >
                    {t.app.graph.open}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(null);
                      coreServices.navigation.goGraph({
                        wsName,
                        centerWsPath: selected.id,
                      });
                    }}
                    className="shrink-0 rounded px-2 py-1 text-primary text-xs hover:bg-muted"
                  >
                    {t.app.graph.local.short}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </PageContentContainer>
    </>
  );
}
